import type { ComponentType } from 'react';
import { UsefulLinksCard } from './modules/platform/UsefulLinksCard.js';

/**
 * A configuração da base, como moldura de cartões.
 *
 * ## Por que este arquivo existe, e o que ele resolve
 *
 * `Configuração da base` é uma tela do módulo **plataforma**, e ela desenha as
 * chaves de `CONFIGURATION_CATALOG` — que são da plataforma. Em 16/09/2026 a
 * **taxa por boleto** da bonificação precisou aparecer ali: ela vale para a
 * praça inteira e não cabe dentro de nenhum consultor, que é onde as premiações
 * passaram a morar.
 *
 * Importá-la direto em `TenantSettingsScreen.tsx` **não compila**, e a régua
 * está certa: módulo não importa de módulo. Uma tela da plataforma que conhece
 * o cadastro do comercial é o começo de uma tela que conhece os sete.
 *
 * Este registro é a mesma saída de `panels.ts` para o início: cada módulo
 * publica o próprio cartão e registra uma linha aqui; a tela itera a lista e
 * não conhece nenhum deles. Cartão novo é uma linha — nenhum módulo edita
 * `TenantSettingsScreen.tsx`.
 *
 * ## O que NÃO entra aqui
 *
 * Configuração que é chave → valor entra em `CONFIGURATION_CATALOG`, e aparece
 * na tela sem passar por este arquivo. Isto é para o cadastro que a praça
 * inteira usa e que tem **forma própria** — a taxa tem uma linha por
 * competência, e uma chave única guardaria um valor só: reapurar dezembro
 * passaria a cobrar a taxa de hoje.
 *
 * ## O recurso é conferido
 *
 * Um `resource` com typo faz o cartão sumir para todo mundo sem erro em lugar
 * nenhum — `canAccess` nega por omissão, corretamente. É a mesma armadilha que
 * `registry.test.ts` cobre em `menu.ts` e `panels.ts`, e pela mesma razão.
 */
export interface SettingsCard {
  /** identificador estável — some da tela se o módulo sair */
  key: string;
  component: ComponentType;
  /** quem não pode ver não vê o cartão. Sem recurso, aparece para todo mundo. */
  resource?: string;
  action?: string;
}

/* --- `taxa-da-premiacao` (AwardFeeCard) SAIU em 21/09/2026 --------------- *
 *
 * Era o único cartão desta lista. Declarava a taxa física por boleto que a
 * bonificação descontava, por competência.
 *
 * A tarifa passou a vir do espelho — `tarifa_cobranca_banco`, o que o banco
 * cobrou naquele boleto —, e não há o que digitar. Ver
 * `packages/domain/src/awards.ts`.
 *
 * **A lista fica vazia, e o arquivo fica.** Ela é o registro por onde um
 * cartão de configuração entra, com o recurso que o esconde; apagá-la faria o
 * próximo nascer dentro de `SettingsScreen.tsx`, que é exatamente o que este
 * arquivo existe para impedir — o mesmo desenho de `panels.ts`.
 * ------------------------------------------------------------------------- */
export const SETTINGS_CARDS: SettingsCard[] = [
  {
    /*
     * Os links úteis da base (07/10/2026). Cadastrados aqui e mostrados em
     * Plataforma → Links úteis a todo mundo da praça. O cartão lista para quem
     * vê a configuração; os botões de cadastrar são de quem a edita.
     */
    key: 'links-uteis',
    component: UsefulLinksCard,
    resource: 'platform.tenants',
    action: 'ver',
  },
  /*
   * O cartão "perda da Carteira" (`LosingBoletoTypesCard`) saiu daqui em
   * 16/10/2026: o lado "Você está perdendo" passou a contar os tipos de cada
   * premiação, e a lista da praça que ele editava deixou de ser lida. Um
   * cartão que grava uma régua que nada usa é a tela afirmando o que o código
   * não cumpre.
   */
];
